import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import { DS } from '../theme';

interface PricingPageProps {
  onBack: () => void;
  onSelectPlan: (plan: string) => void;
}

const plans = [
  {
    name: 'Starter',
    price: 'Free',
    tagline: 'Forever free',
    badge: null,
    description: 'Perfect for individuals exploring their data.',
    features: [
      'Up to 3 projects',
      '5 datasets',
      'Basic charts & visualizations',
      '10 AI queries / month',
      'CSV & Excel upload',
    ],
    cta: 'Get Started Free',
    highlight: false,
  },
  {
    name: 'Pro',
    price: 'Free',
    tagline: 'During beta',
    badge: 'Most Popular',
    description: 'For power users who want unlimited access.',
    features: [
      'Unlimited projects',
      'Unlimited datasets',
      'All chart types',
      '100 AI queries / month',
      'Data manipulation tools',
      'Export to CSV & PDF',
    ],
    cta: 'Get Started Free',
    highlight: true,
  },
  {
    name: 'Team',
    price: 'Free',
    tagline: 'During beta',
    badge: null,
    description: 'Collaborate with your team on shared datasets.',
    features: [
      'Everything in Pro',
      'Up to 5 team members',
      'Shared projects',
      'Unlimited AI queries',
      'Priority support',
      'Custom branding',
    ],
    cta: 'Get Started Free',
    highlight: false,
  },
];

const PricingPage: React.FC<PricingPageProps> = ({ onBack, onSelectPlan }) => {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: DS.bg, display: 'flex', flexDirection: 'column' }}>

      {/* ── Top bar ── */}
      <Box sx={{
        height: 56, display: 'flex', alignItems: 'center', px: 3,
        backgroundColor: DS.bg2, borderBottom: `1px solid ${DS.border}`,
        flexShrink: 0,
      }}>
        <Button startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} onClick={onBack}
          sx={{ color: DS.textMuted, fontSize: '0.875rem' }}>
          Back
        </Button>
        <Box sx={{ flex: 1 }} />
        <Box onClick={onBack} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
          <Box sx={{ width: 26, height: 26, backgroundColor: DS.accent, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>DS</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: DS.text, letterSpacing: '-0.3px' }}>DataScope</Typography>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', px: { xs: 3, md: 6 }, py: { xs: 6, md: 8 } }}>

        {/* Heading */}
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: DS.accent, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2 }}>
          Pricing
        </Typography>
        <Typography sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 700, color: DS.text, letterSpacing: '-0.03em', textAlign: 'center', mb: 1 }}>
          Simple, transparent pricing
        </Typography>
        <Typography sx={{ fontSize: '1rem', color: DS.textMuted, textAlign: 'center', mb: 1.5, maxWidth: 480 }}>
          All plans are completely free right now during our beta.
        </Typography>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 1,
          px: '12px', py: '5px', borderRadius: '9999px',
          backgroundColor: DS.amberDim, border: `1px solid ${DS.amberBorder}`, mb: 6,
        }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: DS.amber }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: DS.amber }}>
            Beta pricing — everything free until launch
          </Typography>
        </Box>

        {/* Plan cards */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: '16px',
          width: '100%',
          maxWidth: 920,
        }}>
          {plans.map((plan) => (
            <Box
              key={plan.name}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: plan.highlight ? DS.bg3 : DS.bg2,
                border: `1px solid ${plan.highlight ? DS.accent : DS.border}`,
                borderRadius: '14px',
                p: '28px 24px',
                transition: 'border-color 0.15s, transform 0.15s',
                '&:hover': {
                  borderColor: plan.highlight ? DS.accent : DS.borderStrong,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* Popular badge */}
              {plan.badge && (
                <Box sx={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  backgroundColor: DS.accent, px: '12px', py: '4px', borderRadius: '9999px',
                  whiteSpace: 'nowrap',
                }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#fff', letterSpacing: '0.04em' }}>
                    {plan.badge}
                  </Typography>
                </Box>
              )}

              {/* Plan name */}
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.textFaint, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>
                {plan.name}
              </Typography>

              {/* Price */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', mb: '4px' }}>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: DS.text, letterSpacing: '-1.5px', lineHeight: 1 }}>
                  {plan.price}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '12px', color: DS.accent, fontWeight: 600, mb: 2 }}>
                {plan.tagline}
              </Typography>

              <Typography sx={{ fontSize: '13px', color: DS.textMuted, lineHeight: 1.55, mb: 3 }}>
                {plan.description}
              </Typography>

              {/* Features */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', mb: 3 }}>
                {plan.features.map((f) => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Box sx={{
                      width: 18, height: 18, borderRadius: '50%',
                      backgroundColor: plan.highlight ? DS.accentDim : DS.bg3,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <CheckIcon sx={{ fontSize: 11, color: DS.accent }} />
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>{f}</Typography>
                  </Box>
                ))}
              </Box>

              {/* CTA */}
              <Button
                variant={plan.highlight ? 'contained' : 'outlined'}
                fullWidth
                onClick={() => onSelectPlan(plan.name)}
                sx={{
                  py: 1.25, fontWeight: 600, fontSize: '0.9375rem', borderRadius: '8px',
                  ...(plan.highlight ? {
                    boxShadow: '0 4px 16px rgba(107,94,248,0.3)',
                  } : {}),
                }}
              >
                {plan.cta}
              </Button>
            </Box>
          ))}
        </Box>

        <Typography sx={{ fontSize: '0.8125rem', color: DS.textFaint, mt: 4, textAlign: 'center' }}>
          No credit card required · Cancel anytime · Free during beta
        </Typography>
      </Box>
    </Box>
  );
};

export default PricingPage;
